import { inject, injectable } from "@launchtray/tsyringe-async";
import { IGameViewModel } from "../scenes/game/models/IGameViewModel";
import { ISettingsController } from "./ISettingsController";
import { ISettingsModel } from "./ISettingsModel";
import { ShopModule } from "../scenes/popups/shop/ShopModule";
import { IContextResolver } from "../composition/IContextResolver";
import { AsyncOperationNotification } from "../scenes/common/components/AsyncOperationNotification";
import { MyWalletModule } from "../scenes/popups/myWallet/MyWalletModule";
import { ILogger } from "../logger/ILogger";
import { UserProfileResult } from "../scenes/popups/userprofile/profile/UserProfileResult";
import { UserProfileModule } from "../scenes/popups/userprofile/UserProfileModule";

@injectable()
export class SettingsController implements ISettingsController {
    private readonly settingsModel: ISettingsModel;
    private readonly gameViewModel: IGameViewModel;
    private readonly contextResolver: IContextResolver;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly logger: ILogger;

    public constructor(
        @inject("ISettingsModel") settingsModel: ISettingsModel,
        @inject("IGameViewModel") gameViewModel: IGameViewModel,
        @inject("IContextResolver") contextResolver: IContextResolver,
        asyncOperationNotification: AsyncOperationNotification,
        @inject("ILogger") logger: ILogger
    ) {
        this.settingsModel = settingsModel;
        this.gameViewModel = gameViewModel;
        this.contextResolver = contextResolver;
        this.asyncOperationNotification = asyncOperationNotification;
        this.logger = logger;
    }

    public switchSoundEnabled(): void {
        this.settingsModel.switchSoundEnabled();
    }

    public async showUserProfilePopup(): Promise<void> {
        this.gameViewModel.popupShown();
        const userProfileModule = await this.asyncOperationNotification.autoShowHide(this.contextResolver.resolve(UserProfileModule));
        const result = await userProfileModule.start();
        if (result === UserProfileResult.SHOW_MY_WALLET) {
            await this.showMyWalletPopup();
        }
        this.gameViewModel.popupHidden();
    }

    public async showShopPopup(): Promise<void> {
        this.gameViewModel.popupShown();
        const shopModule = await this.asyncOperationNotification.autoShowHide(this.contextResolver.resolve(ShopModule));
        await shopModule.start();
        this.gameViewModel.popupHidden();
    }

    private async showMyWalletPopup(): Promise<void> {
        try {
            this.asyncOperationNotification.show();
            const preloaderModule = await this.contextResolver.resolve(MyWalletModule);
            this.asyncOperationNotification.hide();
            await preloaderModule.start();
        }
        catch (e: unknown) {
            this.logger.error("Exception on showing MyWalletPopup: ", e);
            this.asyncOperationNotification.hide();
        }
    }
}
